<template>
  <!-- 操作栏 -->
  <el-row justify="end">
    <el-button @click="openForm">
      <Icon class="mr-5px" icon="ep:opportunity" />
      创建商机
    </el-button>
    <el-button
      @click="openBusinessModal"
      v-hasPermi="['crm:contact:create-business']"
      v-if="queryParams.contactId"
    >
      <Icon class="mr-5px" icon="ep:circle-plus" />关联
    </el-button>
    <el-button
      @click="deleteContactBusinessList"
      v-hasPermi="['crm:contact:delete-business']"
      v-if="queryParams.contactId"
    >
      <Icon class="mr-5px" icon="ep:remove" />解除关联
    </el-button>
  </el-row>

  <!-- 列表 -->
  <ContentWrap class="mt-10px">
    <el-table
      ref="businessRef"
      v-loading="loading"
      :data="list"
      :stripe="true"
      :show-overflow-tooltip="true"
    >
      <el-table-column type="selection" width="55" v-if="queryParams.contactId" />
      <!-- ★ 列 / 列宽与「商机管理」(views/crm/business/index.vue) 保持一致 (2026-09-20)
           有意差异：
           ① 不显示「客户名称」列 —— 本组件只用于客户详情 / 联系人详情的「商机」页签，
              列表已按单个客户过滤，该列恒为同一个值，纯占宽度。
           ② 不带「操作」列 —— 这里是查看 + 新建入口，编辑/删除请去商机管理。
           ③ 商机状态组 / 商机阶段 不做 fixed="right" —— 详情页容器比管理页窄，
              再固定两列会把可滚动区挤没。
           ④ 顺带修 bug：原「商机金额」列 prop="price"，但 CrmBusinessRespVO 里
              **没有** price 字段（只有 totalPrice）⇒ 该列此前一直显示空白。 -->
      <el-table-column align="center" fixed="left" label="商机名称" prop="name" width="160">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column
        :formatter="erpPriceTableColumnFormatter"
        align="center"
        label="商机金额（元）"
        prop="totalPrice"
        width="140"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="预计成交日期"
        prop="dealTime"
        width="180px"
      />
      <el-table-column align="center" label="备注" prop="remark" width="200" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="下次联系时间"
        prop="contactNextTime"
        width="180px"
      />
      <el-table-column align="center" label="负责人" prop="ownerUserName" width="100px" />
      <el-table-column align="center" label="所属部门" prop="ownerUserDeptName" width="100px" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="最后跟进时间"
        prop="contactLastTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="更新时间"
        prop="updateTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180px"
      />
      <el-table-column align="center" label="创建人" prop="creatorName" width="100px" />
      <el-table-column align="center" label="商机状态组" prop="statusTypeName" width="140" />
      <el-table-column align="center" label="商机阶段" prop="statusName" width="120" />
    </el-table>
    <!-- 分页 -->
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加 -->
  <BusinessForm ref="formRef" @success="getList" />
  <!-- 关联商机选择弹框 -->
  <BusinessListModal
    ref="businessModalRef"
    :customer-id="props.customerId!"
    @success="createContactBusinessList"
  />
</template>
<script setup lang="ts">
import * as BusinessApi from '@/api/crm/business'
import * as ContactApi from '@/api/crm/contact'
import BusinessForm from './../BusinessForm.vue'
import { BizTypeEnum } from '@/api/crm/permission'
import BusinessListModal from './BusinessListModal.vue'
import { erpPriceTableColumnFormatter } from '@/utils'
import { dateFormatter } from '@/utils/formatTime'

const message = useMessage() // 消息

defineOptions({ name: 'CrmBusinessList' })
const props = defineProps<{
  bizType: number // 业务类型
  bizId: number // 业务编号
  customerId?: number // 关联联系人与商机时，需要传入 customerId 进行筛选
  contactId?: number // 特殊：联系人编号；在【联系人】详情中，可以传递联系人编号，默认新建的商机关联到该联系人
}>()

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 列表的数据
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  customerId: undefined as unknown, // 允许 undefined + number
  contactId: undefined as unknown // 允许 undefined + number
})

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    // 置空参数
    queryParams.customerId = undefined
    queryParams.contactId = undefined
    // 执行查询
    let data = { list: [], total: 0 }
    switch (props.bizType) {
      case BizTypeEnum.CRM_CUSTOMER:
        queryParams.customerId = props.bizId
        data = await BusinessApi.getBusinessPageByCustomer(queryParams)
        break
      case BizTypeEnum.CRM_CONTACT:
        queryParams.contactId = props.bizId
        data = await BusinessApi.getBusinessPageByContact(queryParams)
        break
      default:
        return
    }
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 添加操作 */
const formRef = ref()
const openForm = () => {
  formRef.value.open('create', null, props.customerId, props.contactId)
}

/** 打开联系人详情 */
const { push } = useRouter()
const openDetail = (id: number) => {
  push({ name: 'CrmBusinessDetail', params: { id } })
}

/** 打开联系人与商机的关联弹窗 */
const businessModalRef = ref()
const openBusinessModal = () => {
  businessModalRef.value.open()
}
const createContactBusinessList = async (businessIds: number[]) => {
  const data = {
    contactId: props.bizId,
    businessIds: businessIds
  } as ContactApi.ContactBusinessReqVO
  businessRef.value.getSelectionRows().forEach((row: BusinessApi.BusinessVO) => {
    data.businessIds.push(row.id)
  })
  await ContactApi.createContactBusinessList(data)
  // 刷新列表
  message.success('关联商机成功')
  handleQuery()
}

/** 解除联系人与商机的关联 */
const businessRef = ref()
const deleteContactBusinessList = async () => {
  const data = {
    contactId: props.bizId,
    businessIds: businessRef.value.getSelectionRows().map((row: BusinessApi.BusinessVO) => row.id)
  } as ContactApi.ContactBusinessReqVO
  if (data.businessIds.length === 0) {
    return message.error('未选择商机')
  }
  await ContactApi.deleteContactBusinessList(data)
  // 刷新列表
  message.success('取关商机成功')
  handleQuery()
}

/** 监听打开的 bizId + bizType，从而加载最新的列表 */
watch(
  () => [props.bizId, props.bizType],
  () => {
    handleQuery()
  },
  { immediate: true, deep: true }
)
</script>
